import { type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import { type FormActionType } from '@blaze-shared/workflow-domain';

import { patchTitle } from '../../../ai/page/prop-builders';

import { type FormContainerResolvedContext } from './form-container.definition';
import { FormElementHandler } from './form.element-handler';

export class FormContainerElementHandler extends FormElementHandler<'formContainer'> {
  readonly type = 'formContainer';

  protected getFormProps(context: FormContainerResolvedContext): PartialPropsOf<'formContainer'> {
    let props: PartialPropsOf<'formContainer'> = {
      content: {},
    };

    props = patchTitle<'formContainer'>(props, context.element.title, context.element.showTitle);

    if (context.element.iconName) {
      props = applyPropsPatch(props, {
        content: {
          title: {
            icon: context.element.iconName,
          },
        },
      });
    }

    if (context.workflow) {
      props = applyPropsPatch(props, {
        data: [
          {
            dataSource: {
              sourceType: 'context',
              id: context.workflow.id,
              columns: [],
            },
            key: 'value',
            bindings: {},
            enabled: true,
          },
        ],
      });
    }

    if (context.element.action) {
      const action = context.element.action.$type === 'add' ? 'insert' : 'update';
      props = applyPropsPatch(props, {
        content: { action },
      });

      if (context.element.action.$type === 'edit') {
        props = applyPropsPatch(props, {
          content: {
            options: {
              addIfMissing: context.element.action.addIfRowDoesntExist,
            },
          },
        });
      }
    }

    if (context.element.layout?.$type === 'custom') {
      props = applyPropsPatch(props, {
        content: { aiGeneratedFields: true },
      });
    }

    const columnCount = this.getColumnCount(context);
    if (columnCount) {
      props = applyPropsPatch(props, {
        content: {
          options: {
            columnsCount: columnCount,
          },
        },
      });
    }

    if (context.element.markRequiredFieldsWithStar !== undefined) {
      props = applyPropsPatch(props, {
        content: {
          options: {
            markRequiredFields: context.element.markRequiredFieldsWithStar,
          },
        },
      });
    }

    if (context.element.submitOnEnter !== undefined) {
      props = applyPropsPatch(props, {
        content: {
          options: {
            submitOnEnter: context.element.submitOnEnter,
          },
        },
      });
    }

    if (context.element.changeOnBlur !== undefined) {
      props = applyPropsPatch(props, {
        content: {
          options: {
            changeOnBlur: context.element.changeOnBlur,
          },
        },
      });
    }

    return props;
  }

  protected getFormActionType(context: FormContainerResolvedContext): FormActionType | undefined {
    switch (context.element.action?.$type) {
      case 'add':
        return 'insert';
      case 'edit':
        return 'update';
      default:
        return undefined;
    }
  }

  protected getSubmitButtonTitleOverrides(
    context: FormContainerResolvedContext,
  ): { label?: string; iconName?: string } | undefined {
    if (context.element.layout?.$type !== 'auto') {
      return undefined;
    }

    const { submitLabel, submitIconName } = context.element.layout;
    return submitLabel || submitIconName
      ? { label: submitLabel, iconName: submitIconName }
      : undefined;
  }
}
